import type {RepositoryPickerRepository} from '@github-ui/item-picker/RepositoryPicker'
import type {AssigneePickerAssignee} from '@github-ui/item-picker-assignee-tsq'
import type {IssueTypePickerIssueType} from '@github-ui/item-picker-issue-type'
import type {RelayCompat} from '@github-ui/server-persisted-queries/relay-compat'
import {useCallback} from 'react'
import {graphql, readInlineData} from 'relay-runtime'

import type {useHandleTemplateChange$key} from './__generated__/useHandleTemplateChange.graphql'
import type {useHandleTemplateChangeIssueForm$key} from './__generated__/useHandleTemplateChangeIssueForm.graphql'
import {useIssueCreateActions, useIssueCreateMetadata} from './contexts/IssueCreateDataContext'
import type {IssueTemplateNode} from './create-issue-query-types'
import {DisplayMode} from './utils/display-mode'
import {
  instanceOfBlankIssueData,
  instanceOfIssueFormData,
  instanceOfIssueTemplateData,
  type IssueCreatePayload,
  IssueCreationKind,
} from './utils/model'
import type {IssueCreateOptionConfig} from './utils/option-config'
import type {IssueCreateValueTypes} from './utils/template-args'
import {getTemplateUrl} from './utils/urls'

type useHandleTemplateChangeProps = {
  optionConfig: IssueCreateOptionConfig
  repository: RepositoryPickerRepository | undefined
  navigate: (url: string) => void
  setDisplayMode: (mode: DisplayMode) => void
}

// RELAY-CLEANUP(useHandleTemplateChange): widens nullable fields so the still-Relay parent
// data is assignable. Remove when useHandleTemplateChange leaves Relay.
export type UseHandleTemplateChangeIssueTemplateFragment = RelayCompat<
  Pick<IssueTemplateNode, '__typename' | 'name' | 'filename' | 'body' | 'title' | 'labels' | 'assignees' | 'type'>
>

// eslint-disable-next-line @github-ui/github-monorepo/restrict-relay-queries
export const UseHandleTemplateChangeIssueFormFragment = graphql`
  fragment useHandleTemplateChangeIssueForm on IssueForm @inline {
    # this field can go once we refactor the issues create dialog
    __id
    __typename
    name
    filename
    title
    elements {
      __typename
      ... on IssueFormElementInput {
        itemId: id
        label
        descriptionHTML
        placeholder
        value
        required
      }
      ... on IssueFormElementTextarea {
        itemId: id
        label
        descriptionHTML
        placeholder
        value
        required
        render
      }
      ... on IssueFormElementMarkdown {
        contentHTML
      }
      ... on IssueFormElementDropdown {
        itemId: id
        label
        descriptionHTML
        options
        required
        multiple
        defaultOptionIndex: default
      }
      ... on IssueFormElementCheckboxes {
        itemId: id
        label
        descriptionHTML
        checkboxOptions: options {
          label
          labelHTML
          required
        }
      }
      ... on IssueFormElementUpload {
        itemId: id
        label
        descriptionHTML
        required
        accept
      }
    }
    labels(first: 20, orderBy: {field: NAME, direction: ASC}) {
      edges {
        node {
          id
          color
          name
          nameHTML
          description
          url
        }
      }
    }
    assignees(first: 10) {
      edges {
        node {
          ... on Actor {
            __typename
            id
            login
            name
            profileResourcePath
            avatarUrl(size: 64)
          }
        }
      }
    }
    projects(first: 20) {
      edges {
        node {
          id
          title
          closed
          number
          url
          viewerCanUpdate
          hasReachedItemsLimit
          __typename
        }
      }
    }
    type {
      id
      name
      isEnabled
      description
      color
      pinnedFields {
        __typename
        ... on IssueFieldText {
          id
          name
          dataType
        }
        ... on IssueFieldSingleSelect {
          id
          name
          dataType
        }
        ... on IssueFieldDate {
          id
          name
          dataType
        }
        ... on IssueFieldNumber {
          id
          name
          dataType
        }
        ... on IssueFieldMultiSelect {
          id
          name
          dataType
        }
      }
    }
  }
`

export const getSelectedTemplate = (repository: useHandleTemplateChange$key): IssueCreatePayload | undefined => {
  // eslint-disable-next-line no-restricted-syntax
  const data = readInlineData(
    // eslint-disable-next-line @github-ui/github-monorepo/restrict-relay-queries
    graphql`
      fragment useHandleTemplateChange on Repository
      @inline
      @argumentDefinitions(filename: {type: "String!", defaultValue: ""}) {
        issueTemplate(filename: $filename) {
          __typename
          name
          filename
          body
          title
          labels(first: 20, orderBy: {field: NAME, direction: ASC}) {
            edges {
              node {
                id
                color
                name
                nameHTML
                description
                url
              }
            }
          }
          assignees(first: 10) {
            edges {
              node {
                ... on Actor {
                  __typename
                  id
                  login
                  name
                  profileResourcePath
                  avatarUrl(size: 64)
                }
              }
            }
          }
          type {
            id
            name
            isEnabled
            description
            color
            pinnedFields {
              __typename
              ... on IssueFieldText {
                id
                name
                dataType
              }
              ... on IssueFieldSingleSelect {
                id
                name
                dataType
              }
              ... on IssueFieldDate {
                id
                name
                dataType
              }
              ... on IssueFieldNumber {
                id
                name
                dataType
              }
              ... on IssueFieldMultiSelect {
                id
                name
                dataType
              }
            }
          }
        }

        issueForm(filename: $filename) {
          ...useHandleTemplateChangeIssueForm
        }
      }
    `,
    repository,
  )
  let fetchedTemplate = undefined
  if (data.issueTemplate) {
    const templateData = data.issueTemplate
    fetchedTemplate = {
      name: templateData.name,
      fileName: templateData.filename,
      kind: IssueCreationKind.IssueTemplate,
      // RELAY-CLEANUP(useHandleTemplateChange): cast needed while the parent fragment is still Relay-owned.
      data: templateData as UseHandleTemplateChangeIssueTemplateFragment,
    }
  } else if (data.issueForm) {
    // eslint-disable-next-line no-restricted-syntax
    const templateData = readInlineData<useHandleTemplateChangeIssueForm$key>(
      UseHandleTemplateChangeIssueFormFragment,
      data.issueForm,
    )
    fetchedTemplate = {
      name: templateData.name,
      fileName: templateData.filename,
      kind: IssueCreationKind.IssueForm,
      data: templateData,
    }
  }
  return fetchedTemplate
}

export const useHandleTemplateChange = ({
  optionConfig,
  repository,
  navigate,
  setDisplayMode,
}: useHandleTemplateChangeProps) => {
  const {setTemplate} = useIssueCreateMetadata()
  const {reinitTitle, reinitBody, reinitAssignees, reinitLabels, reinitProjects, reinitMilestone, reinitIssueType} =
    useIssueCreateActions()

  return useCallback(
    (selectedTemplate?: IssueCreatePayload, enforcedInitialValues?: IssueCreateValueTypes, enforceNavigate = false) => {
      setTemplate(selectedTemplate)

      let newTitle = ''
      if (enforcedInitialValues?.discussion) {
        newTitle = enforcedInitialValues.discussion.title
      } else if (enforcedInitialValues?.title) {
        newTitle = enforcedInitialValues.title || ''
      } else {
        const templateTitle = selectedTemplate?.data.title || ''
        let userInputTitle = ''
        if (enforcedInitialValues?.appendTitleToTemplate) {
          userInputTitle = ` ${enforcedInitialValues.appendTitleToTemplate}`
        }
        newTitle = [templateTitle, userInputTitle].join('').trimStart()
      }

      reinitTitle(newTitle)

      let newBody = ''
      if (enforcedInitialValues?.discussion) {
        newBody = enforcedInitialValues.discussion.formattedBody || ''
      } else if (enforcedInitialValues?.body) {
        newBody = enforcedInitialValues.body || ''
      } else if (
        selectedTemplate &&
        (instanceOfIssueTemplateData(selectedTemplate.data) || instanceOfBlankIssueData(selectedTemplate.data))
      ) {
        newBody = selectedTemplate.data.body || ''
      }
      reinitBody(newBody)

      let templateLabels = []
      if (enforcedInitialValues?.discussion) {
        templateLabels = enforcedInitialValues.discussion?.labels || []
      } else if (enforcedInitialValues?.labels) {
        templateLabels = enforcedInitialValues.labels
      } else {
        templateLabels = selectedTemplate?.data.labels?.edges?.flatMap(e => (e?.node ? [e?.node] : [])) || []
      }

      reinitLabels(templateLabels)

      const templateAssignees =
        enforcedInitialValues?.assignees ??
        (((selectedTemplate?.data.assignees?.edges || []).flatMap(e =>
          e?.node ? [e?.node] : [],
        ) as AssigneePickerAssignee[]) ||
          [])
      reinitAssignees(templateAssignees)

      // Modal will be shown when user clicks "Create issue" if Copilot is in assignees

      if (enforcedInitialValues?.projects) {
        reinitProjects(enforcedInitialValues.projects)
      } else if (selectedTemplate && instanceOfIssueFormData(selectedTemplate?.data)) {
        const templateProjects = selectedTemplate.data.projects?.edges?.flatMap(e => (e?.node ? [e.node] : [])) || []
        reinitProjects(templateProjects)
      }

      if (enforcedInitialValues?.milestone) {
        reinitMilestone(enforcedInitialValues?.milestone)
      }

      let templateType: IssueTypePickerIssueType | null = enforcedInitialValues?.type ?? null
      if (
        selectedTemplate &&
        !templateType &&
        (instanceOfIssueFormData(selectedTemplate.data) || instanceOfIssueTemplateData(selectedTemplate.data)) &&
        selectedTemplate.data.type !== undefined
      ) {
        templateType = selectedTemplate.data.type || null
      }
      reinitIssueType(templateType ?? null)

      if (!enforcedInitialValues || enforceNavigate) {
        if (optionConfig.navigateToFullScreenOnTemplateChoice && repository && selectedTemplate) {
          navigate(getTemplateUrl(repository.nameWithOwner, selectedTemplate.fileName))
        } else {
          setDisplayMode(DisplayMode.IssueCreation)
        }
      }
    },
    [
      setTemplate,
      reinitTitle,
      reinitBody,
      reinitLabels,
      reinitAssignees,
      reinitIssueType,
      reinitProjects,
      reinitMilestone,
      optionConfig.navigateToFullScreenOnTemplateChoice,
      repository,
      navigate,
      setDisplayMode,
    ],
  )
}
